import React, { useCallback, useEffect, useRef } from "react";
import AsyncSelect from "react-select/async";
import { useTranslation } from "react-i18next";
import Label from "components/Input/Label/Label";
import HelperMessage from "components/Input/HelperMessage/HelperMessage";
import s from "./AsyncAutocomplete.module.scss";

const AsyncAutocomplete = ({
  fetchOptions,
  value,
  onChange,
  label,
  placeholder,
  mandatory = false,
  disabled = false,
  error = false,
  errorMessage,
  noErrorMessage = false,
  preloadedOptions = [],
  sx,
  onKeyDown,
  isCacheOptions = true,
}) => {
  const { t } = useTranslation();
  const debounceTimerRef = useRef();

  const loadOptions = useCallback(
    (keyword, callback) => {
      const searchKeyword = `${keyword || ""}`.trim();

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      if (!searchKeyword || !fetchOptions) {
        callback(preloadedOptions);
        return undefined;
      }

      debounceTimerRef.current = setTimeout(async () => {
        try {
          const result = await fetchOptions(searchKeyword);
          callback(Array.isArray(result) ? result : []);
        } catch (err) {
          console.error(err);
          callback([]);
        }
      }, 500);

      return undefined;
    },
    [fetchOptions, preloadedOptions],
  );

  const handleInputChange = (nextInputValue, { action }) => {
    if (action === "input-change" && value) {
      onChange?.(null, null);
    }
    return nextInputValue;
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <div style={sx}>
      {label && <Label label={label} mandatory={mandatory} />}
      <AsyncSelect
        cacheOptions={isCacheOptions}
        defaultOptions={preloadedOptions}
        loadOptions={loadOptions}
        value={value}
        isDisabled={disabled}
        isClearable
        placeholder={placeholder}
        className={
          error
            ? `${s.asyncAutocomplete} ${s.asyncAutocompleteError}`
            : s.asyncAutocomplete
        }
        classNamePrefix="async-autocomplete"
        menuPortalTarget={
          typeof document === "undefined" ? undefined : document.body
        }
        menuPosition="fixed"
        onChange={(nextValue) => onChange?.(null, nextValue)}
        onInputChange={handleInputChange}
        noOptionsMessage={({ inputValue: currentInputValue }) =>
          currentInputValue
            ? t("component.asyncAutocomplete.noResults")
            : t("component.asyncAutocomplete.typeToSearch")
        }
        onKeyDown={onKeyDown}
      />
      {!noErrorMessage && <HelperMessage message={error && errorMessage} />}
    </div>
  );
};

export default AsyncAutocomplete;
